import { InboxSocketEvent } from '@bandido/shared/constants';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useSiteConfig } from '@frontend/features/common/components/SiteConfigContext';
import { useWS } from '@frontend/features/common/hooks/useWS';
import { CONFIG } from '@frontend/config';

import { isInboxEnabled } from './access';
import { syncInboxAppBadge } from './appBadge';
import { inboxQueryKeys, useInboxUnreadCount } from './queries';
import { createInboxRefreshScheduler } from './refreshScheduler';

interface InboxContextValue {
  hasInboxAccess: boolean;
  unreadCaseCount: number;
  isOnline: boolean;
  isRealtimeReady: boolean;
}

const InboxContext = createContext<InboxContextValue>({
  hasInboxAccess: false,
  unreadCaseCount: 0,
  isOnline: true,
  isRealtimeReady: false,
});

export const useInbox = () => useContext(InboxContext);

export const InboxProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const { ability, myProfile } = useAuth();
  const { settings } = useSiteConfig();
  const enabled = isInboxEnabled(settings?.featureFlags) && ability.can('manage', 'Cases');
  const userId = myProfile?.id;
  const { data } = useInboxUnreadCount(userId, enabled);
  const { socketRef, isConnected } = useWS(`${CONFIG.WS_URL}/v1/api/inbox`, { enabled });
  const [isOnline, setIsOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [isRealtimeReady, setIsRealtimeReady] = useState(false);
  const unreadCaseCount = enabled ? (data?.unreadCaseCount ?? 0) : 0;

  useEffect(() => {
    void syncInboxAppBadge(unreadCaseCount);
  }, [unreadCaseCount]);

  useEffect(() => {
    if (!isConnected) setIsRealtimeReady(false);
  }, [isConnected]);

  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, []);

  useEffect(() => {
    const socket = enabled ? socketRef.current : undefined;
    if (!socket) return;

    const scheduler = createInboxRefreshScheduler(() => {
      if (!userId) return;
      queryClient.invalidateQueries(inboxQueryKeys.lists(userId));
      queryClient.invalidateQueries(inboxQueryKeys.unread(userId));
      queryClient.invalidateQueries(inboxQueryKeys.histories(userId));
      queryClient.invalidateQueries(inboxQueryKeys.contexts(userId));
    });
    const refresh = scheduler.schedule;
    const ready = () => {
      setIsRealtimeReady(true);
      refresh();
    };
    const disconnected = () => setIsRealtimeReady(false);
    socket.on(InboxSocketEvent.READY, ready);
    socket.on(InboxSocketEvent.INVALIDATED, refresh);
    socket.on('disconnect', disconnected);
    return () => {
      scheduler.cancel();
      socket.off(InboxSocketEvent.READY, ready);
      socket.off(InboxSocketEvent.INVALIDATED, refresh);
      socket.off('disconnect', disconnected);
    };
  }, [enabled, queryClient, socketRef, userId]);

  return (
    <InboxContext.Provider value={{ hasInboxAccess: enabled, unreadCaseCount, isOnline, isRealtimeReady }}>
      {children}
    </InboxContext.Provider>
  );
};
